import React, { useState, useEffect } from 'react';
import { getProducts, updateProduct, deleteProduct } from '../firebase/services';
import '../styles/CategoryUpdater.css';

const CategoryUpdater = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [log, setLog] = useState([]);

  const categoryUpdates = {
    'mawa kachori': { from: 'Sweets', to: 'Namkeen and Snacks' },
    'gajar mix': { from: 'Namkeen and Snacks', to: 'Juice and Shake' },
    'roasted kaju': { from: 'Namkeen and Snacks', to: 'Sweets' },
    'rosted kaju': { from: 'Namkeen and Snacks', to: 'Sweets' },
    'milk': { from: 'Juice and Shake', to: 'Dairy and Beverages' },
    'pista kulfi': { from: 'Sweets', to: 'Ice Cream and Desserts' },
    'kesar kulfi': { from: 'Sweets', to: 'Ice Cream and Desserts' },
    'falooda': { from: 'Sweets', to: 'Ice Cream and Desserts' },
    'sundae': { from: 'Sweets', to: 'Ice Cream and Desserts' },
    'ice cream cone': { from: 'Sweets', to: 'Ice Cream and Desserts' },
    'kulfi': { from: 'Sweets', to: 'Ice Cream and Desserts' },
  };

  const itemsToDelete = [
    'masala tea', 'lemon tea', 'coffee', 'black tea', 'green tea',
    'chai', 'espresso', 'cappuccino', 'latte', 'tea'
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const productsData = await getProducts();
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      addLog('❌ Error loading products: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addLog = (message, type = 'info') => {
    setLog(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
  };

  const updateCategories = async () => {
    if (!window.confirm('Are you sure you want to update categories? This will modify your database.')) {
      return;
    }

    setUpdating(true);
    setLog([]);
    addLog('🚀 Starting category updates...', 'info');

    let updatedCount = 0;
    let deletedCount = 0;

    for (const product of products) {
      const productName = product.name.toLowerCase();

      // Check if product should be deleted
      const shouldDelete = itemsToDelete.some(item => 
        productName.includes(item.toLowerCase())
      );

      if (shouldDelete) {
        try {
          await deleteProduct(product.id);
          addLog(`❌ DELETED: ${product.name}`, 'delete');
          deletedCount++;
        } catch (error) {
          addLog(`⚠️ Failed to delete ${product.name}: ${error.message}`, 'error');
        }
        continue;
      }

      // Check if product needs category update
      for (const [searchName, update] of Object.entries(categoryUpdates)) {
        if (productName.includes(searchName.toLowerCase())) {
          if (product.category === update.from || !product.category) {
            try {
              await updateProduct(product.id, { category: update.to });
              addLog(`✅ UPDATED: ${product.name}`, 'success');
              addLog(`   ${update.from || 'No category'} → ${update.to}`, 'info');
              updatedCount++;
            } catch (error) {
              addLog(`⚠️ Failed to update ${product.name}: ${error.message}`, 'error');
            }
          }
          break;
        }
      }
    }

    addLog('\n=== Summary ===', 'info');
    addLog(`✅ Updated: ${updatedCount} products`, 'success');
    addLog(`❌ Deleted: ${deletedCount} products`, 'delete');
    addLog('🎉 Done!', 'success');

    setUpdating(false);
    
    // Reload products
    setTimeout(() => {
      loadProducts();
    }, 2000);
  };

  const previewChanges = () => {
    setLog([]);
    addLog('📋 Preview of changes that will be made:', 'info');
    addLog('', 'info');

    let updateCount = 0;
    let deleteCount = 0;

    products.forEach(product => {
      const productName = product.name.toLowerCase();

      // Check if product should be deleted
      const shouldDelete = itemsToDelete.some(item => 
        productName.includes(item.toLowerCase())
      );

      if (shouldDelete) {
        addLog(`❌ WILL DELETE: ${product.name}`, 'delete');
        deleteCount++;
        return;
      }

      // Check if product needs category update
      for (const [searchName, update] of Object.entries(categoryUpdates)) {
        if (productName.includes(searchName.toLowerCase())) {
          if (product.category === update.from || !product.category) {
            addLog(`✅ WILL UPDATE: ${product.name}`, 'success');
            addLog(`   ${product.category || 'No category'} → ${update.to}`, 'info');
            updateCount++;
          }
          break;
        }
      }
    });

    addLog('', 'info');
    addLog(`Total updates: ${updateCount}`, 'info');
    addLog(`Total deletions: ${deleteCount}`, 'info');
  };

  if (loading) {
    return <div className="category-updater"><div className="loading">Loading products...</div></div>;
  }

  return (
    <div className="category-updater">
      <h1>Category Updater Tool</h1>
      <p className="description">
        This tool will update product categories and delete tea/coffee items from your database.
      </p>

      <div className="actions">
        <button onClick={previewChanges} className="btn-preview" disabled={updating}>
          👁️ Preview Changes
        </button>
        <button onClick={updateCategories} className="btn-update" disabled={updating}>
          {updating ? '⏳ Updating...' : '🚀 Update Categories'}
        </button>
      </div>

      <div className="stats">
        <div className="stat-card">
          <h3>Total Products</h3>
          <p>{products.length}</p>
        </div>
        <div className="stat-card">
          <h3>Categories</h3>
          <p>{new Set(products.map(p => p.category).filter(Boolean)).size}</p>
        </div>
      </div>

      {log.length > 0 && (
        <div className="log-container">
          <h2>Activity Log</h2>
          <div className="log">
            {log.map((entry, index) => (
              <div key={index} className={`log-entry log-${entry.type}`}>
                <span className="log-time">[{entry.time}]</span>
                <span className="log-message">{entry.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="changes-list">
        <h2>Planned Changes</h2>
        <div className="changes-grid">
          <div className="change-section">
            <h3>Category Updates</h3>
            <ul>
              {Object.entries(categoryUpdates).map(([name, update]) => (
                <li key={name}>
                  <strong>{name}</strong>: {update.from} → {update.to}
                </li>
              ))}
            </ul>
          </div>
          <div className="change-section">
            <h3>Items to Delete</h3>
            <ul>
              {itemsToDelete.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryUpdater;
